import { useEffect, useState } from "react";
import { CustomText, isCustomText, Passage, InputRow } from "../../utils/inputRow";
import TableDropdown from "./TableDropdown";
import TableTextInput from "./TableTextInput";
import ToggleButton from "./ToggleButton";
import { getStartupData, Translation } from "../../services/startupService";

interface Props {
  first?: boolean;
  last?: boolean;
  rowInput?: InputRow;
  fixed: boolean;
  onComplete: (e: InputRow) => void;
  onUp: (id: number) => void;
  onDown: (id: number) => void;
  onDelete: (id: number) => void;
}

const PassageTableRow = (props: Props) => {
  const [allBooks, setAllBooks] = useState<string[]>([]);
  const [allTranslations, setAllTranslations] = useState<Translation[]>([]);
  const [custom, setCustom] = useState(props.rowInput !== undefined && isCustomText(props.rowInput));
  const [rowInput, setRowInput] = useState<InputRow>(
    props.rowInput ??
      (custom ? { id: -1, reference: "", text: "" } : { id: -1, book: "", chapter: "", verses: "", translation: "" })
  );

  useEffect(() => {
    getStartupData().then((data) => {
      setAllBooks(data.books);
      setAllTranslations(data.translations);
    });
  }, []);

  const customTextModifier = (mutator: (o: CustomText) => void) => {
    const customText = rowInput as CustomText;
    const copy = { id: customText.id, reference: customText.reference, text: customText.text };
    mutator(copy);
    setRowInput(copy);
    if (isValidContent(copy)) {
      props.onComplete(copy);
    }
  };

  const passageModifier = (mutator: (o: Passage) => void) => {
    const passage = rowInput as Passage;
    const copy = {
      id: passage.id,
      book: passage.book,
      chapter: passage.chapter,
      verses: passage.verses,
      translation: passage.translation,
    };
    mutator(copy);
    setRowInput(copy);
    if (isValidContent(copy)) {
      props.onComplete(copy);
    }
  };

  const isValidContent = (e: InputRow): boolean => {
    if (isCustomText(e)) {
      return true;
    }
    const p = e as Passage;
    return p.book !== "" && p.verses !== "" && p.translation !== "";
  };

  const onCustomToggle = (checked: boolean) => {
    if (checked) {
      setRowInput({ id: -1, reference: "", text: "" });
    } else {
      setRowInput({ id: -1, book: "", chapter: "", verses: "", translation: "" });
    }
    setCustom(checked);
  };

  const customReferenceUpdate = (e: string) => {
    customTextModifier((c) => (c.reference = e));
  };

  const customTextUpdate = (e: string) => {
    customTextModifier((c) => (c.text = e));
  };

  const bookUpdate = (e: string) => {
    passageModifier((p) => (p.book = e));
  };

  const chapterUpdate = (e: string) => {
    passageModifier((p) => (p.chapter = e));
  };

  const versesUpdate = (e: string) => {
    passageModifier((p) => (p.verses = e));
  };

  const translationUpdate = (e: string) => {
    passageModifier((p) => (p.translation = e));
  };

  const speechBubbleIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="rgb(51,65,85)"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
      />
    </svg>
  );
  const openBookIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="rgb(51,65,85)"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
  const bookDropdown = (
    <TableDropdown selection={(rowInput as Passage).book} entries={allBooks} onSelection={bookUpdate} />
  );
  const translationDropdown = (
    <TableDropdown
      selection={(rowInput as Passage).translation}
      entries={allTranslations.map((e) => e.name + " (" + e.code + ")")}
      onSelection={translationUpdate}
    />
  );
  const chapterInput = (
    <TableTextInput
      text={(rowInput as Passage).chapter}
      regex="^[1-9][0-9]{0,2}$"
      onTextAvailable={chapterUpdate}
      placeholder="Chapter"
    />
  );
  const versesInput = (
    <TableTextInput
      text={(rowInput as Passage).verses}
      regex="^[1-9][0-9]{0,2}?(-[1-9][0-9]{0,2}?)?$"
      onTextAvailable={versesUpdate}
      placeholder="Verses"
    />
  );
  const toggleButton = <ToggleButton checked={custom} onChange={onCustomToggle} />;
  const referenceInput = (
    <TableTextInput
      text={(rowInput as CustomText).reference}
      regex="^.*$"
      onTextAvailable={customReferenceUpdate}
      placeholder="Reference"
    />
  );
  const textInput = (
    <TableTextInput
      text={(rowInput as CustomText).text}
      regex="^.*$"
      onTextAvailable={customTextUpdate}
      placeholder="Text"
    />
  );
  const toogleIconTable = !props.fixed ? (
    toggleButton
  ) : (
    <div className="pl-[9px]">{custom ? speechBubbleIcon : openBookIcon}</div>
  );
  const optionButtons = (
    <div className="flex">
      {!props.first ? (
        <button
          className="w-[36px] pb-1 text-red-700 text-xl font-bold text-sky-600 hover:text-sky-700 rounded"
          onClick={() => props.onUp(rowInput.id)}
        >
          {"\u2191"}
        </button>
      ) : (
        <div className="w-[36px]"></div>
      )}
      {!props.last ? (
        <button
          className="w-[36px] pb-1 text-red-700 text-xl font-bold text-sky-600 hover:text-sky-700 rounded"
          onClick={() => props.onDown(rowInput.id)}
        >
          {"\u2193"}
        </button>
      ) : (
        <div className="w-[36px]"></div>
      )}
      <button
        className="w-[36px] pb-1 text-red-700 text-xl font-bold text-red-600 hover:text-red-700 rounded"
        onClick={() => props.onDelete(rowInput.id)}
      >
        {"\u00D7"}
      </button>
    </div>
  );
  return (
    <>
      <div
        className="
      hidden
      md:grid
      md:grid-cols-[minmax(40px,1fr)_minmax(160px,2fr)_minmax(70px,1fr)_minmax(70px,1fr)_minmax(190px,3fr)_minmax(70px,1fr)]
      md:gap-x-3
      lg:grid-cols-[minmax(40px,1fr)_minmax(160px,2fr)_minmax(70px,1fr)_minmax(70px,1fr)_minmax(320px,3fr)_minmax(70px,1fr)]
      lg:gap-x-6
      font-bold
      text-slate-700
      border-t py-1
    "
      >
        {<div className="flex items-center">{toogleIconTable}</div>}
        {custom ? <div className="col-span-2">{referenceInput}</div> : bookDropdown}
        {custom ? <div className="col-span-2">{textInput}</div> : chapterInput}
        {custom ? null : versesInput}
        {custom ? null : translationDropdown}
        {props.fixed ? <div className="flex items-center justify-center">{optionButtons}</div> : null}
      </div>

      <div
        className={`md:hidden mb-2 grid grid-cols-[min-content,1fr] gap-x-4 items-center pt-4 ${!props.first ? "border-t" : ""}`}
      >
        <span className="font-bold text-slate-700">Type:</span>
        {!props.fixed ? toggleButton : custom ? speechBubbleIcon : openBookIcon}
        {custom ? (
          <>
            <span className="font-bold text-slate-700">Reference:</span>
            {referenceInput}
            <span className="font-bold text-slate-700">Text:</span>
            {textInput}
          </>
        ) : (
          <>
            <span className="font-bold text-slate-700">Book:</span>
            {bookDropdown}
            <span className="font-bold text-slate-700">Chapter:</span>
            <div className="max-w-[70px]">{chapterInput}</div>
            <span className="font-bold text-slate-700">Verses:</span>
            <div className="max-w-[70px]">{versesInput}</div>
            <span className="font-bold text-slate-700">Translation:</span>
            {translationDropdown}
          </>
        )}
        <span className="font-bold text-slate-700">Options:</span>
        {props.fixed ? optionButtons : null}
      </div>
    </>
  );
};

export default PassageTableRow;
